import { Component, Injectable, OnInit } from '@angular/core';
import { first } from 'rxjs/operators';
import { UserLogin, Email } from '../../shared/models/user.model';
import { AuthService } from '../../shared/services/auth.service';
import { USER_KEY } from '../../shared/contants/data-model';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CoreService } from '../../shared/services/core.service';
import Swal from 'sweetalert2';
import { IClubInvite, ILeagueInvite } from '../../shared/models/club.model';
import { NgModel } from '@angular/forms';

@Injectable()
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  id: string | null = null;
  clubId: string | null = null;
  leagueId: string | null = null;
  loginUser: UserLogin = new UserLogin();
  welcomeMsg: any = null;
  loginError: string | null = null;
  emailmodel: Email = new Email();
  loginSuccess: boolean = false;
  hidePassword: boolean = true;
  isCombined: boolean = false;
  redirectUrl: string | null = null;
  constructor(private router: Router, private auth: AuthService, private route: ActivatedRoute, private coreService: CoreService) { }

 ngOnInit(): void {
  this.auth.logOut();

  // 🔹 Handle email from route param (unchanged)
  this.route.paramMap.subscribe((params: ParamMap) => {
    const email = params.get('email');
    if (email) {
      this.loginUser.email = email!;
      this.emailmodel.email = email;
      this.emailmodel.canEditEmail = false;
      this.auth.emailCheck.next(this.emailmodel);
    }
  });

  // 🔥 MAIN QUERY PARAM HANDLER (UPDATED)
  this.route.queryParams.subscribe((params: any) => {

    this.id = params?.id;
    this.leagueId = params?.leagueId;
    this.clubId = params?.clubId;
    this.isCombined = params?.isCombined === 'true';
    this.redirectUrl = params?.redirect || null;

    // 🔥 NEW COMBINED FLOW
    if (this.isCombined && this.id && this.leagueId && this.clubId) {
      this.getLeagueDetails(); // reuse existing method
    }

    // 👇 EXISTING LEAGUE FLOW (UNCHANGED)
    else if (this.leagueId && this.id) {

      const currentUrl = this.router.url;

      if (
        !currentUrl.includes('leagueId') ||
        currentUrl.indexOf('leagueId') > currentUrl.indexOf('id')
      ) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { leagueId: this.leagueId, id: this.id },
          queryParamsHandling: '',
          replaceUrl: true
        });
      }

      this.getLeagueDetails();
    }

    // 👇 EXISTING CLUB FLOW (UNCHANGED)
    else if (this.id && !this.leagueId) {
      this.getInvitationDetails();
    }

  });

  // 🔹 Email observable (unchanged)
  this.auth.emailObs$.subscribe((emailmodel: Email) => {
    this.emailmodel = emailmodel;
    this.loginUser.email = emailmodel.email;
  });
}

getEmailErrorMessage(emailControl: NgModel): string {
  if (emailControl.errors?.['required']) {
    return 'Email is required';
  }
  if (emailControl.errors?.['email']) {
    return 'Invalid email format';
  }
  return '';
}

  getInvitationDetails() {
    this.coreService.isLoading.next(true);
    this.coreService.getApi('/api/Club/GetInvitationDetails?encryptedData=' + this.id).subscribe({
      next: (res: IClubInvite) => {
        this.coreService.isLoading.next(false);
        if (res.clubId) {
          this.clubId = res.clubId.toString();
        }
      },
      error: (error) => {
        this.coreService.isLoading.next(false);
      },
    });
  }

  getLeagueDetails() {
    this.coreService.isLoading.next(true);
    this.coreService.getApi('/api/League/GetLeagueInvitationDetails?encryptedData=' + this.id).subscribe({
      next: (res: ILeagueInvite) => {
        this.coreService.isLoading.next(false);
        if (res.clubId) {
          this.clubId = res.clubId.toString();
        }
        if (res.leagueId) {
          this.leagueId = res.leagueId.toString();
        }
      },
      error: (error) => {
        this.coreService.isLoading.next(false);
      },
    });
  }

  forgotPassword() {
    Swal.fire({
      title: "Forgot Password",
      text: "Enter your email to reset your password:",
      input: "email",
      inputPlaceholder: "Enter your email",
      showCancelButton: true,
      confirmButtonText: "Submit",
      confirmButtonColor: "var(--primary-blue)",
      cancelButtonText: "Cancel",
      cancelButtonColor: "var(--dk-orange)",
      inputValidator: (value) => {
        if (!value) {
          return "Email is required!";
        }
        if (!/^\S+@\S+\.\S+$/.test(value)) {
          return "Please enter a valid email address!";
        }
        return undefined;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.reSetPasswordEmail(result.value);
      }
    });
  }

  reSetPasswordEmail(email: string) {
    this.coreService.isLoading.next(true);
    this.coreService.getAnonymousApi(`/api/User/ReSetPasswordEmail?email=${email}`, {responseType : 'text'}).subscribe({
      next: (res : any) => {
        this.coreService.isLoading.next(false);
        if (res == "Success") {
          Swal.fire({
            icon: "success",
            title: "Email Sent!",
            text: "Check your inbox for the reset link.",
            showConfirmButton: true,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res,
            showConfirmButton: true,
          });
        }
      },
      error: (error) => {
        this.coreService.isLoading.next(false);
      },
    });
  }

  async LogIn(user: UserLogin, clubId = this.clubId, leagueId = this.leagueId) {
    this.coreService.isLoading.next(true);
    try {
      const msg: any = await this.auth.login(user, clubId, this.id, leagueId).pipe(first()).toPromise();
      this.coreService.isLoading.next(false);
      this.welcomeMsg = msg;
      if (msg !== 'Invalid Password' && msg !== 'Invalid Userid') {
        if (this.redirectUrl) {
          this.router.navigateByUrl(this.redirectUrl);
          return;
        }

        if (this.id && leagueId && this.isCombined) {
  // 🔥 Step 1: Go ONLY to club page first
  this.router.navigate(
    ['/club', clubId],
    { 
      queryParams: { 
        id: this.id,
        leagueId: leagueId,
        isCombined: true
      } 
    }
  );
}
      else if (this.id && leagueId) {
        // League invite FIRST
        this.router.navigate(
          ['/club', clubId, leagueId],
          { queryParams: { id: this.id } }
        );
      }
      else if (this.id && clubId) {
        // Club invite
        this.router.navigate(
          ['/club', clubId],
          { queryParams: { id: this.id } }
        );
      }
      else {
        this.router.navigate(['/home']);
      }
      } else {
        this.loginError = msg;
        Swal.fire('Oops...', 'Invalid email or password', 'error');
      }
    } catch (err: any) {
      this.coreService.isLoading.next(false);
      this.loginError = err.message || 'An error occurred.';
      Swal.fire('Oops...', 'Something went wrong, please try again', 'error');
    }
  }
}
